import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Search,
  Link2,
  Target,
  CalendarDays,
  BarChart3,
  Trash2,
  ChevronLeft,
  Menu,
  X,
  Bot,
  Sparkles,
  Settings,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DiagnosticsPanel from '@/components/ios26/DiagnosticsPanel';
import { SidebarStepItem } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * Project Steps Configuration
 */
const GUIDED_STEPS = [
  { 
    id: 'analyze', 
    step: 1, 
    icon: '🔍', 
    title: 'Analyser mon site',
    path: 'setup/analyze',
    description: 'L\'IA analyse votre contenu'
  },
  { 
    id: 'connect', 
    step: 2, 
    icon: '🔌', 
    title: 'Connecter mes comptes',
    path: 'setup/connect',
    description: 'Reliez vos réseaux sociaux'
  },
  { 
    id: 'campaign', 
    step: 3, 
    icon: '🎯', 
    title: 'Créer une campagne',
    path: 'setup/campaign',
    description: 'Définissez vos objectifs'
  },
  { 
    id: 'generate', 
    step: 4, 
    icon: '🗓', 
    title: 'Générer 30 jours',
    path: 'setup/generate',
    description: 'Un mois de contenu'
  },
  { 
    id: 'results', 
    step: 5, 
    icon: '📊', 
    title: 'Résultats',
    path: 'setup/results',
    description: 'Suivez vos performances'
  }
];

const UTILITY_ITEMS = [
  {
    id: 'calendar',
    icon: Calendar,
    title: 'Calendrier',
    path: 'calendar'
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics',
    path: 'analytics'
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Paramètres',
    path: 'settings'
  }
];

/**
 * ProjectLayout - iOS 26-Inspired Project Layout
 * ==============================================
 * Unified layout for all /projects/:id/* routes
 * - Left sidebar with guided steps
 * - Main content area
 * - Right diagnostics panel (collapsible)
 */
export default function ProjectLayout({ children }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepStatuses, setStepStatuses] = useState({});
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Load project data
  useEffect(() => {
    loadProject();
  }, [projectId]);
  
  const loadProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      const data = response.data;
      setProject(data);
      
      // Determine step statuses
      const statuses = {
        analyze: data.website_analyzed ? 'completed' : 'pending',
        connect: determineConnectStatus(data),
        campaign: data.campaign_created ? 'completed' : 'pending',
        generate: data.content_generated ? 'completed' : 'pending',
        results: data.has_analytics ? 'completed' : 'pending'
      };
      setStepStatuses(statuses);
    } catch (error) {
      toast.error('Erreur lors du chargement du projet');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const determineConnectStatus = (project) => {
    const channels = project?.channels_config || {};
    const hasAnyConnected = Object.values(channels).some(ch => ch?.enabled);
    const hasAnyError = Object.values(channels).some(ch => ch?.error);
    
    if (hasAnyError) return 'warning';
    if (hasAnyConnected) return 'completed';
    return 'pending';
  };
  
  const getCurrentStep = () => {
    const path = location.pathname;
    
    // Check guided steps
    const guidedStep = GUIDED_STEPS.find(s => path.includes(s.path));
    if (guidedStep) return guidedStep.id;
    
    // Check utility items
    const utilityItem = UTILITY_ITEMS.find(u => path.includes(u.path));
    if (utilityItem) return utilityItem.id;
    
    return null;
  };
  
  const isStepLocked = (stepId) => {
    const stepIndex = GUIDED_STEPS.findIndex(s => s.id === stepId);
    if (stepIndex <= 0) return false;
    
    const prevStep = GUIDED_STEPS[stepIndex - 1];
    return stepStatuses[prevStep.id] !== 'completed';
  };
  
  const handleStepClick = (step) => {
    if (isStepLocked(step.id)) {
      toast.info('Complétez l\'étape précédente d\'abord');
      return;
    }
    setMobileMenuOpen(false);
    navigate(`/projects/${projectId}/${step.path}`);
  };
  
  const handleUtilityClick = (item) => {
    setMobileMenuOpen(false);
    navigate(`/projects/${projectId}/${item.path}`);
  };
  
  const currentStep = getCurrentStep();
  
  if (loading) {
    return (
      <div className="min-h-screen ios-bg-mesh flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen ios-bg-mesh">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <Menu className="w-5 h-5" />
      </button>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* ============================================
          LEFT SIDEBAR
          ============================================ */}
      <aside className={cn(
        'fixed top-0 left-0 z-40 h-full w-72 backdrop-blur-xl',
        'flex flex-col transition-transform duration-300',
        'lg:translate-x-0',
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}
      style={{
        backgroundColor: 'hsl(var(--sidebar-bg))',
        borderRight: '1px solid hsl(var(--color-border))'
      }}
      >
        {/* Header */}
        <div className="p-5" style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}>
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm transition-colors"
              style={{ color: 'hsl(var(--sidebar-text))' }}
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden p-1.5 rounded-lg"
              style={{ backgroundColor: 'hsl(var(--sidebar-item-hover))' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {project?.name?.charAt(0)?.toUpperCase() || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold truncate text-semantic">{project?.name || 'Mon projet'}</h2>
              <p className="text-xs truncate text-semantic-muted">{project?.website_url || 'Pas de site'}</p>
            </div>
          </div>
        </div>
        
        {/* Guided Steps */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="text-xs font-semibold uppercase tracking-wider px-3 mb-2 text-semantic-muted">
            Configuration guidée
          </div>
          
          <div className="space-y-1 mb-6">
            {GUIDED_STEPS.map((step) => {
              const status = stepStatuses[step.id] || 'pending';
              const locked = isStepLocked(step.id);
              const isActive = currentStep === step.id;
              
              let stepStatus = 'pending';
              if (status === 'completed') stepStatus = 'completed';
              else if (status === 'error') stepStatus = 'error';
              else if (status === 'warning') stepStatus = 'warning';
              else if (isActive) stepStatus = 'active';
              
              return (
                <SidebarStepItem
                  key={step.id}
                  step={step.step}
                  title={step.title}
                  icon={step.icon}
                  status={stepStatus}
                  locked={locked}
                  onClick={() => handleStepClick(step)}
                />
              );
            })}
          </div>
          
          {/* Utility Items */}
          <div className="text-xs font-semibold uppercase tracking-wider px-3 mb-2 text-semantic-muted">
            Outils
          </div>
          
          <div className="space-y-1">
            {UTILITY_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = currentStep === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleUtilityClick(item)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all',
                    isActive 
                      ? 'font-medium' 
                      : ''
                  )}
                  style={{
                    backgroundColor: isActive ? 'hsl(var(--sidebar-item-active-bg))' : 'transparent',
                    color: isActive ? 'hsl(var(--sidebar-text-active))' : 'hsl(var(--sidebar-text))'
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {item.title}
                </button>
              );
            })}
          </div>
        </nav>
        
        {/* Footer - AI Assistant Toggle */}
        <div className="p-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
          <button
            onClick={() => setShowDiagnostics(!showDiagnostics)}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all',
              showDiagnostics 
                ? 'shadow-lg shadow-blue-500/25' 
                : ''
            )}
            style={{
              backgroundColor: showDiagnostics ? 'hsl(var(--color-primary))' : 'hsl(var(--color-surface-2))',
              color: showDiagnostics ? 'hsl(var(--color-primary-text))' : 'hsl(var(--sidebar-text))'
            }}
          >
            <Bot className="w-5 h-5" />
            Assistant IA
          </button>
        </div>
      </aside>
      
      {/* ============================================
          MAIN CONTENT
          ============================================ */}
      <main className={cn(
        'transition-all duration-300 min-h-screen',
        'lg:ml-72',
        showDiagnostics && 'lg:mr-96'
      )}>
        {/* Top Bar */}
        <header 
          className="sticky top-0 z-30 backdrop-blur-xl"
          style={{
            backgroundColor: 'hsl(var(--color-surface) / 0.7)',
            borderBottom: '1px solid hsl(var(--color-border) / 0.5)'
          }}
        >
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-3 lg:hidden">
              {/* Space for mobile menu button */}
              <div className="w-10" />
            </div>
            
            <div className="flex-1 flex items-center justify-center lg:justify-start">
              {currentStep && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-semantic-muted">
                    {GUIDED_STEPS.find(s => s.id === currentStep)?.icon || 
                     UTILITY_ITEMS.find(u => u.id === currentStep) && '⚙️'}
                  </span>
                  <span className="font-medium text-semantic">
                    {GUIDED_STEPS.find(s => s.id === currentStep)?.title ||
                     UTILITY_ITEMS.find(u => u.id === currentStep)?.title}
                  </span>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all'
              )}
              style={{
                backgroundColor: showDiagnostics ? 'hsl(var(--color-primary))' : 'hsl(var(--color-surface-2))',
                color: showDiagnostics ? 'hsl(var(--color-primary-text))' : 'hsl(var(--color-text-2))'
              }}
            >
              <Bot className="w-4 h-4" />
              <span className="hidden sm:inline">Aide</span>
            </button>
          </div>
        </header>
        
        {/* Page Content */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* ============================================
          DIAGNOSTICS PANEL (Right Side)
          ============================================ */}
      {showDiagnostics && (
        <DiagnosticsPanel 
          onClose={() => setShowDiagnostics(false)}
          projectId={projectId}
        />
      )}
    </div>
  );
}

export { GUIDED_STEPS, UTILITY_ITEMS };
